/**
 * Crea tu pack — centre-focused carousel + live pack builder.
 * - Carousel: the card nearest the viewport centre is "active" (shows the
 *   "Añadir al pack" button); prev/next scroll between cards.
 * - Panel: live progress bar, item count, line items with qty steppers,
 *   discount preview and total. "Añadir al carrito" adds every unit to the
 *   cart and opens the drawer.
 */
(function () {
  'use strict';

  const money = (cents) => {
    const v = (Math.round(cents) / 100).toFixed(2);
    let [int, dec] = v.split('.');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${int},${dec}€`;
  };

  const initPack = (section) => {
    const track = section.querySelector('[data-pack-track]');
    if (!track) return;

    const allCards = Array.from(track.querySelectorAll('[data-pack-card]'));
    // Cards of the active tab (all of them when there are no tabs).
    let cards = allCards.filter((c) => !c.hidden);
    const tabsEl = section.querySelector('[data-pack-tabs]');
    const prevBtn = section.querySelector('[data-pack-prev]');
    const nextBtn = section.querySelector('[data-pack-next]');

    const progressText = section.querySelector('[data-pack-progress-text]');
    const barFill = section.querySelector('[data-pack-bar]');
    const countEl = section.querySelector('[data-pack-count]');
    const itemsEl = section.querySelector('[data-pack-items]');
    const emptyEl = section.querySelector('[data-pack-empty]');
    const discountRow = section.querySelector('[data-pack-discount-row]');
    const discountEl = section.querySelector('[data-pack-discount]');
    const totalEl = section.querySelector('[data-pack-total]');
    const checkoutBtn = section.querySelector('[data-pack-checkout]');
    const checkoutWrap = section.querySelector('[data-pack-checkout-wrap]');
    const tooltipEl = section.querySelector('[data-pack-tooltip]');

    // Storefront-language strings rendered by the section (data-i18n).
    let I18N = {};
    try { I18N = JSON.parse(section.dataset.i18n || '{}'); } catch (e) { I18N = {}; }
    const tr = (key, vars) => (I18N[key] || '').replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] != null ? vars[k] : m));

    const CHECK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path fill="currentColor" d="m9.55 15.15l8.475-8.475q.3-.3.7-.3t.7.3t.3.713t-.3.712l-9.175 9.2q-.3.3-.7.3t-.7-.3L4.55 13q-.3-.3-.288-.712t.313-.713t.713-.3t.712.3z"/></svg>';

    const minItems = parseInt(section.dataset.min, 10) || 3;
    const targetQty = parseInt(section.dataset.target, 10) || minItems;
    const discountPct = parseFloat(section.dataset.discount) || 0;
    const discountCode = (section.dataset.discountCode || '').trim();
    // Optional tier 2 (more units, higher %): only the highest tier reached applies.
    let targetQty2 = parseInt(section.dataset.target2, 10) || 0;
    let discountPct2 = parseFloat(section.dataset.discount2) || 0;
    const discountCode2 = (section.dataset.discountCode2 || '').trim();
    if (targetQty2 <= targetQty || discountPct2 <= 0) { targetQty2 = 0; discountPct2 = 0; }
    // Equipment only counts toward the minimum/discount when the merchant says so.
    const equipCounts = section.dataset.equipCounts === 'true';
    // Equipment only joins the discount tier/amount when the Shopify discount also covers it.
    const equipDiscount = section.dataset.equipDiscount === 'true';

    // Active tier for a given unit count.
    const tierFor = (count) => {
      if (targetQty2 && count >= targetQty2) return { pct: discountPct2, code: discountCode2 || discountCode };
      if (discountPct > 0 && count >= targetQty) return { pct: discountPct, code: discountCode };
      return null;
    };

    // -- State -------------------------------------------------------------
    const pack = new Map(); // variantId -> { qty, title, price, image, sellingPlan, compare }

    const counted = (it) => !it.equip || equipCounts;
    const totalUnits = () => Array.from(pack.values()).reduce((s, it) => s + it.qty, 0);
    // Units that count toward the minimum and the discount tiers.
    const countedUnits = () => Array.from(pack.values()).reduce((s, it) => s + (counted(it) ? it.qty : 0), 0);
    const subtotalCents = () => Array.from(pack.values()).reduce((s, it) => s + it.price * it.qty, 0);
    const discountCounted = (it) => !it.equip || equipDiscount;
    const discountUnits = () => Array.from(pack.values()).reduce((s, it) => s + (discountCounted(it) ? it.qty : 0), 0);
    const discountBaseCents = () => Array.from(pack.values()).reduce((s, it) => s + (discountCounted(it) ? it.price * it.qty : 0), 0);

    const render = () => {
      const count = discountUnits(); // tier progress: what the Shopify discount really counts
      const ucount = countedUnits(); // items that satisfy the pack minimum
      const allUnits = totalUnits();
      const subtotal = subtotalCents();
      const tier = tierFor(count);
      const discountActive = !!tier;
      const discountCents = tier ? Math.round(discountBaseCents() * tier.pct / 100) : 0;
      const totalCents = subtotal - discountCents;

      // Progress text + bar
      if (progressText) {
        if (discountPct > 0) {
          if (count < targetQty) {
            const left = targetQty - count;
            progressText.textContent = tr('progress_discount', { left: left, pct: discountPct });
          } else if (targetQty2 && count < targetQty2) {
            const left = targetQty2 - count;
            progressText.textContent = tr('progress_tier', { left: left, pct: discountPct, pct2: discountPct2 });
          } else {
            progressText.textContent = tr('progress_applied', { pct: tier ? tier.pct : discountPct });
          }
        } else if (ucount < minItems) {
          const left = minItems - ucount;
          progressText.textContent = tr(left === 1 ? 'progress_complete_one' : 'progress_complete_other', { left: left });
        } else {
          progressText.textContent = tr('ready');
        }
      }
      if (barFill) {
        // The bar fills towards the next milestone (tier 1, then tier 2).
        let ref = discountPct > 0 ? targetQty : minItems;
        if (discountPct > 0 && targetQty2 && count >= targetQty) ref = targetQty2;
        const pct = Math.min((discountPct > 0 ? count : ucount) / ref, 1) * 100;
        barFill.style.width = `${pct}%`;
      }
      section.classList.toggle('is-discount-active', discountActive);

      // Count
      if (countEl) countEl.textContent = tr(allUnits === 1 ? 'items_one' : 'items_other', { n: allUnits });

      // Items
      if (itemsEl) {
        itemsEl.innerHTML = '';
        pack.forEach((it, id) => {
          const li = document.createElement('li');
          li.className = 'iurop-pack__item';
          li.innerHTML = `
            ${it.image ? `<img class="iurop-pack__item-img" src="${it.image}" alt="" loading="lazy">` : '<span class="iurop-pack__item-img"></span>'}
            <span class="iurop-pack__item-body">
              <span class="iurop-pack__item-title">${it.title}</span>
              <span class="iurop-pack__item-price">${money(it.price)}</span>
              <span class="iurop-pack__item-qty">
                <button type="button" class="iurop-pack__qty-btn" data-pack-dec aria-label="${tr('dec')}">−</button>
                <span class="iurop-pack__qty-num">${it.qty}</span>
                <button type="button" class="iurop-pack__qty-btn" data-pack-inc aria-label="${tr('inc')}">+</button>
              </span>
            </span>`;
          li.querySelector('[data-pack-dec]').addEventListener('click', () => changeQty(id, -1));
          li.querySelector('[data-pack-inc]').addEventListener('click', () => changeQty(id, 1));
          itemsEl.appendChild(li);
        });
      }
      if (emptyEl) emptyEl.style.display = pack.size ? 'none' : '';

      // Totals
      if (discountRow) discountRow.style.display = discountCents > 0 ? '' : 'none';
      if (discountEl) discountEl.textContent = money(discountCents);
      if (totalEl) totalEl.textContent = money(totalCents);

      // Checkout enabled when minimum met + dynamic tooltip while locked.
      const locked = ucount < minItems;
      if (checkoutBtn) checkoutBtn.disabled = locked;
      if (checkoutWrap) checkoutWrap.classList.toggle('is-locked', locked);
      if (tooltipEl) {
        const left = Math.max(1, minItems - ucount);
        tooltipEl.textContent = tr(left === 1 ? 'tooltip_one' : 'tooltip_other', { left: left });
      }
    };

    const addItem = (data) => {
      const id = data.variantId;
      if (pack.has(id)) {
        pack.get(id).qty += 1;
      } else {
        pack.set(id, {
          qty: 1,
          title: data.title,
          price: data.price,
          image: data.image,
          sellingPlan: data.sellingPlan,
          compare: data.compare,
          equip: !!data.equip,
        });
      }
      render();
    };

    const changeQty = (id, delta) => {
      const it = pack.get(id);
      if (!it) return;
      it.qty += delta;
      if (it.qty <= 0) pack.delete(id);
      render();
    };

    // -- Click a side card to bring it to the centre ----------------------
    track.addEventListener('click', (e) => {
      if (e.target.closest('[data-pack-add]') || e.target.closest('a')) return;
      const card = e.target.closest('[data-pack-card]');
      if (card && !card.classList.contains('is-active')) {
        e.preventDefault();
        scrollToCard(card);
      }
    });

    // -- Add to pack buttons ----------------------------------------------
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pack-add]');
      if (!btn) return;
      addItem({
        variantId: btn.dataset.variantId,
        sellingPlan: btn.dataset.sellingPlan || null,
        title: btn.dataset.title || '',
        price: parseInt(btn.dataset.price, 10) || 0,
        compare: parseInt(btn.dataset.compare, 10) || 0,
        image: btn.dataset.image || '',
        equip: btn.dataset.equip === '1',
      });
      // Brief feedback
      btn.classList.add('is-added');
      const original = btn.textContent;
      btn.innerHTML = `${CHECK_SVG} ${tr('added')}`;
      setTimeout(() => { btn.textContent = original; btn.classList.remove('is-added'); }, 900);
    });

    // -- Checkout ----------------------------------------------------------
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        if (checkoutBtn.disabled) return;
        // Mark every line as part of the pack so the cart drawer can group it.
        const items = [];
        pack.forEach((it, id) => {
          const line = { id: parseInt(id, 10), quantity: it.qty, properties: { _pack: 'Crea tu pack' } };
          if (it.sellingPlan) line.selling_plan = parseInt(it.sellingPlan, 10);
          items.push(line);
        });
        if (!items.length) return;

        const count = discountUnits();
        const tier = tierFor(count);

        checkoutBtn.disabled = true;
        const original = checkoutBtn.textContent;
        checkoutBtn.textContent = tr('adding');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        })
          .then((r) => (r.ok ? r.json() : r.json().then((err) => Promise.reject(err))))
          // Apply the discount code (only on click — no automatic discount).
          .then(() => {
            if (tier && tier.code) {
              // Visiting /discount/<code> stores the code in the cart session
              // (cookie) so it applies at checkout — only on this click.
              return fetch(`/discount/${encodeURIComponent(tier.code)}?redirect=/cart.js`, { credentials: 'same-origin' }).catch(() => {});
            }
          })
          .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }))
          .then((r) => r.json())
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, openDrawer: true } }));
            pack.clear();
            render();
            checkoutBtn.textContent = original;
          })
          .catch((err) => {
            console.error('[pack-builder] add to cart failed', err);
            checkoutBtn.textContent = original;
            checkoutBtn.disabled = false;
          });
      });
    }

    // -- Equipment variant selector: keeps the add button, price and image in
    // sync with the chosen variant.
    track.addEventListener('change', (e) => {
      const sel = e.target.closest('[data-pack-variant]');
      if (!sel) return;
      const opt = sel.options[sel.selectedIndex];
      const card = sel.closest('[data-pack-card]');
      const btn = card && card.querySelector('[data-pack-add]');
      if (!opt || !btn) return;
      const price = parseInt(opt.dataset.price, 10) || 0;
      const compare = parseInt(opt.dataset.compare, 10) || 0;
      btn.dataset.variantId = opt.value;
      btn.dataset.price = price;
      if (compare > price) btn.dataset.compare = compare; else delete btn.dataset.compare;
      if (opt.dataset.image) btn.dataset.image = opt.dataset.image;
      const base = btn.dataset.productTitle || '';
      btn.dataset.title = opt.dataset.vtitle ? `${base} · ${opt.dataset.vtitle}` : base;
      const priceEl = card.querySelector('.cafeshi-mega-card__price');
      if (priceEl) {
        priceEl.textContent = money(price);
        priceEl.classList.toggle('is-discounted', compare > price);
      }
      const cmpEl = card.querySelector('.cafeshi-mega-card__compare');
      if (cmpEl) {
        cmpEl.textContent = compare > price ? money(compare) : '';
        cmpEl.hidden = !(compare > price);
      }
    });

    // -- Tabs (Cafés / Equipamiento) ---------------------------------------
    const setTab = (kind) => {
      allCards.forEach((c) => { c.hidden = c.dataset.packKind !== kind; c.classList.remove('is-active'); });
      cards = allCards.filter((c) => !c.hidden);
      if (tabsEl) {
        tabsEl.querySelectorAll('[data-pack-tab]').forEach((t) => {
          const on = t.dataset.packTab === kind;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      track.scrollLeft = 0;
      requestAnimationFrame(() => {
        const startIndex = cards.length > 2 ? 1 : 0;
        if (cards[startIndex]) scrollToCard(cards[startIndex]);
        updateActive();
      });
    };
    if (tabsEl) {
      tabsEl.addEventListener('click', (e) => {
        const t = e.target.closest('[data-pack-tab]');
        if (t) setTab(t.dataset.packTab);
      });
      // Start on coffees; equipment cards stay hidden until their tab opens.
      allCards.forEach((c) => { c.hidden = c.dataset.packKind === 'equipment'; });
      cards = allCards.filter((c) => !c.hidden);
    }

    // -- Carousel: active card detection ----------------------------------
    let rafPending = false;
    const updateActive = () => {
      rafPending = false;
      const rect = track.getBoundingClientRect();
      const centre = rect.left + rect.width / 2;
      let best = null;
      let bestDist = Infinity;
      cards.forEach((card) => {
        const cr = card.getBoundingClientRect();
        const cc = cr.left + cr.width / 2;
        const d = Math.abs(cc - centre);
        if (d < bestDist) { bestDist = d; best = card; }
      });
      cards.forEach((c) => c.classList.toggle('is-active', c === best));
    };
    const scheduleActive = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(updateActive);
    };

    track.addEventListener('scroll', scheduleActive, { passive: true });
    window.addEventListener('resize', scheduleActive);

    const scrollToCard = (card) => {
      if (!card) return;
      const cr = card.getBoundingClientRect();
      const tr = track.getBoundingClientRect();
      const delta = (cr.left + cr.width / 2) - (tr.left + tr.width / 2);
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };
    const activeIndex = () => cards.findIndex((c) => c.classList.contains('is-active'));
    if (prevBtn) prevBtn.addEventListener('click', () => scrollToCard(cards[Math.max(activeIndex() - 1, 0)]));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollToCard(cards[Math.min(activeIndex() + 1, cards.length - 1)]));

    // Centre the second card initially (mirrors the design's middle focus).
    requestAnimationFrame(() => {
      const startIndex = cards.length > 2 ? 1 : 0;
      if (cards[startIndex]) scrollToCard(cards[startIndex]);
      updateActive();
    });

    // On a product page, start the pack with the product being viewed.
    if (section.dataset.packSelfVariant) {
      addItem({
        variantId: section.dataset.packSelfVariant,
        sellingPlan: section.dataset.packSelfSellingPlan || null,
        title: section.dataset.packSelfTitle || '',
        price: parseInt(section.dataset.packSelfPrice, 10) || 0,
        compare: parseInt(section.dataset.packSelfCompare, 10) || 0,
        image: section.dataset.packSelfImage || '',
      });
    }

    render();
  };

  // Recommendations mode: fetch the section rendered with recommendations,
  // inject the cards, then initialise the pack on the populated element.
  const loadRecommendations = (section) => {
    const url = section.dataset.packRecUrl;
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fetched = doc.querySelector('[data-pack]');
        if (fetched && fetched.querySelector('[data-pack-card]')) {
          section.innerHTML = fetched.innerHTML;
          section.removeAttribute('data-pack-rec-url');
          initPack(section);
        } else {
          // No recommendations available → remove the whole section block.
          const block = section.closest('.shopify-section') || section;
          block.remove();
        }
      })
      .catch(() => {
        const block = section.closest('.shopify-section') || section;
        block.remove();
      });
  };

  const start = (section) => {
    if (section.hasAttribute('data-pack-rec-url')) loadRecommendations(section);
    else initPack(section);
  };

  const boot = () => document.querySelectorAll('[data-pack]').forEach(start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-pack]');
    if (section) start(section);
  });
})();
